/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { FaPencilAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import { atom, useRecoilValue } from "recoil";
import * as CampaignApi from "../../../../../api/Campaign";
import { IReadCampaign } from "../../../../../api/Campaign";
import { IInternalUser } from "../../../../../atoms/UserAtom";
import {
  IAlias,
  ICampaignStatus,
  INodeTypes,
  IUserEmailOptions,
  IUserIRoute,
} from "../../../../../generated/Api";
import { useDefaultAlias } from "../../../../../hook/useDefaultAlias";
import { Flex, SpaceBetween } from "../../../../../styles/general";
import { nanoidForFooterAndAlias } from "../../../../NewEmail/Modals/SavedSearch/FooterSearchModal";
import { StyledRadio } from "../../../../StyledRadio";
import { useModal } from "../../../Modal";
import AddAddresses from "./AddAddresses";

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 25px 0px;
  padding: 0px 22px;
  width: 600px;
  height: fit-content;

  @media screen and (max-width: 600px) {
    width: 100%;
  }
`;

const Title = styled.div`
  color: #2f2f2f;
  font-weight: 600;
  margin-bottom: 5px;
`;

const Name = styled.div`
  color: #2f2f2f;
  font-size: 14px;
`;

const Email = styled.div`
  color: #2e89c6;
  font-size: 14px;
  margin-left: 15px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 300px;

  @media screen and (max-width: 600px) {
    width: 200px;
  }
`;

const Card = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border: 1px solid #eceeee;
  margin: 10px 0px;
  &:first-of-type {
    margin-top: 20px;
  }
`;

const AddAddressesButton = styled.span`
  color: #fb972e;
  font-size: 15px;
  cursor: pointer;
  margin-bottom: 5px;
  font-weight: 600;
`;

export type UserAlias = Omit<IAlias, "id">;

function removeDuplicateUserAliases(aliases: IAlias[]) {
  let userAliasMap: { [userAlias: string]: UserAlias } = {};

  aliases.forEach(
    (alias) => (userAliasMap[`${alias.address}_${alias.fromName}`] = alias)
  );

  return Object.values(aliases);
}

interface Props {
  user: IUserIRoute;
  internalUser: IInternalUser;
  setUser: (u: IUserIRoute) => void;
  temporarySelectionToParent: (index: string) => void;
}

export const CurrentSelectedAliasIdAtom = atom<string | null>({
  key: "CurrentSelectedAliasAtom",
  default: null,
});

export default function AddressesSettings(props: Props): JSX.Element {
  const { user, setUser, internalUser, temporarySelectionToParent } = props;

  const modal = useModal();
  const currentSelectedAlias = useRecoilValue(CurrentSelectedAliasIdAtom);
  const [campaigns, setCampaigns] = useState<IReadCampaign[]>();

  const [temporarySelection, setTemporarySelection] = useState<string | null>(
    null
  );

  const { defaultAlias } = useDefaultAlias({
    //@ts-ignore
    user,
    internalUser,
  });

  function addUserAlias(alias: Required<UserAlias>) {
    if (!user) return null;

    let newAliases = [
      ...user.emailOptions?.aliases,
      { ...alias, id: nanoidForFooterAndAlias() },
    ] as IAlias[];

    setUser({
      ...user,
      emailOptions: {
        ...user.emailOptions,
        aliases: removeDuplicateUserAliases(newAliases),
      },
    });
  }

  function deleteUserAlias(alias: IAlias) {
    const campaignNodeWithAlias = campaigns.find(
      (campaign) =>
        (campaign.status === ICampaignStatus.Active ||
          campaign.status === ICampaignStatus.Inactive ||
          campaign.status === ICampaignStatus.Draft) &&
        !!campaign.nodes.find(
          (node) =>
            node.type === INodeTypes.Email && node.email.aliasId === alias.id
        )
    );

    if (campaignNodeWithAlias) {
      toast("There is a campaign using this send from address", {
        type: "warning",
      });
      return;
    }

    const newAliases = user.emailOptions.aliases.filter(
      (a) => a.id !== alias.id
    );

    setUser({
      ...user,
      emailOptions: {
        ...user.emailOptions,
        aliases: removeDuplicateUserAliases(newAliases),
      },
    });
  }

  function editUserAlias(alias: IAlias) {
    const newAliases = user.emailOptions.aliases.map((a) =>
      a.id === alias.id ? alias : a
    );

    setUser({
      ...user,
      emailOptions: {
        ...user.emailOptions,
        aliases: removeDuplicateUserAliases(newAliases),
      },
    });
  }

  const [aliases, setAliases] = useState<
    Required<Required<IUserEmailOptions>["aliases"][0]>[]
  >([]);

  useEffect(() => {
    (async () => {
      if (campaigns) return;
      const campaignsResponse = await CampaignApi.readAllCampaigns();
      setCampaigns(campaignsResponse);
    })();
  });

  useEffect(() => {
    if (!user) return;
    if (!user.emailOptions) return;

    // Re-order the alias to my user. If I'm a normal user they will be
    // on the same order. If I'm an internal user they COULD be on a different order
    // with the first alias being my internal user selected alias
    const aliasesWithoutMyDefaultAlias = user.emailOptions.aliases.filter(
      (a) => a.id !== defaultAlias.id
    );
    setAliases([defaultAlias, ...aliasesWithoutMyDefaultAlias]);
    setTemporarySelection(defaultAlias.id);
  }, [user, internalUser]);

  useEffect(() => {
    setTemporarySelection(currentSelectedAlias);
  }, [currentSelectedAlias]);

  return (
    <div>
      <FlexColumn>
        <SpaceBetween>
          <Title>Send-From Email Addresses</Title>
          <AddAddressesButton
            onClick={() => {
              modal.pushModal(
                <AddAddresses
                  onCancel={() => modal.popModal()}
                  closeModal={() => modal.popModal()}
                  addUserAlias={addUserAlias}
                  editUserAlias={editUserAlias}
                  deleteUserAlias={deleteUserAlias}

                  // I cannot delete my last one :)
                />,
                "Add Email Address"
              );
            }}
          >
            + Add New
          </AddAddressesButton>
        </SpaceBetween>
        {aliases?.map((alias, index) => {
          return (
            <Card key={`${alias.address || 0 + index}`}>
              <SpaceBetween>
                <Flex>
                  <Name>{alias.fromName}</Name>
                  <Email> &lt;{alias.address}&gt; </Email>
                </Flex>

                <Flex>
                  <FaPencilAlt
                    onClick={() =>
                      modal.pushModal(
                        <AddAddresses
                          onCancel={() => modal.popModal()}
                          closeModal={() => modal.popModal()}
                          addUserAlias={addUserAlias}
                          editUserAlias={editUserAlias}
                          deleteUserAlias={deleteUserAlias}
                          userAliasBeingEdited={{
                            alias,
                            index,
                          }}
                          blockDeletion={user.emailOptions.aliases.length <= 1}
                        />,
                        "Edit Email Address"
                      )
                    }
                    style={{ marginRight: 10, opacity: 0.3, cursor: "pointer" }}
                  />

                  <StyledRadio
                    check={temporarySelection === alias.id}
                    handleCheck={() => {
                      setTemporarySelection(alias.id);
                      temporarySelectionToParent(alias.id);
                    }}
                  />
                </Flex>
              </SpaceBetween>
            </Card>
          );
        })}
      </FlexColumn>
    </div>
  );
}
